import React, { useState, useEffect } from 'react';

const MonthlyRevenueTracker = () => {
  const [paiements, setPaiements] = useState([]);
  const [etudiants, setEtudiants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [anneeScolaire, setAnneeScolaire] = useState('2025/2026');
  const [anneesDisponibles, setAnneesDisponibles] = useState([]);

  // Générer les mois pour une année scolaire (septembre à août)
  const generateMonths = (annee) => {
    const [debut, fin] = annee.split('/').map(Number);
    const months = [];

    for (let i = 0; i < 12; i++) {
      const mois = (8 + i) % 12;
      const anneeActuelle = mois >= 8 ? debut : fin;
      const date = new Date(anneeActuelle, mois, 1);

      months.push({
        key: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
        label: date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }),
        date: date,
        mois: date.toLocaleDateString('fr-FR', { month: 'long' })
      });
    }
    return months;
  };

  const months = generateMonths(anneeScolaire);

  useEffect(() => {
    fetchData();
  }, [anneeScolaire]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // ✅ RÉCUPÉRER TOUS LES PAIEMENTS (y compris archivés)
      const paiementsRes = await fetch('    https://vmi1977988.contaboserver.net/api2/paiements', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const paiementsData = await paiementsRes.json();

      const etudiantsRes = await fetch('    https://vmi1977988.contaboserver.net/api2/etudiants', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const etudiantsData = await etudiantsRes.json();

      setPaiements(paiementsData);
      setEtudiants(etudiantsData);

      const annees = [...new Set(etudiantsData.map(e => e.anneeScolaire).filter(Boolean))].sort().reverse();
      setAnneesDisponibles(annees);

      console.log(`✅ ${paiementsData.length} paiements chargés`);
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  // Calculer les statistiques par mois
  const calculateMonthlyStats = () => {
    const stats = {};
    const otherMonths = {}; // Pour les paiements hors période

    months.forEach(month => {
      stats[month.key] = {
        total: 0,
        count: 0,
        parType: { inscription: 0, formation: 0, autre: 0 }
      };
    });

    paiements.forEach(paiement => {
      const date = new Date(paiement.datePaiement || paiement.createdAt);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

      // ✅ INCLURE TOUS LES PAIEMENTS
      if (stats[monthKey]) {
        stats[monthKey].total += paiement.montant;
        stats[monthKey].count += 1;

        const type = paiement.typePaiement || 'autre';
        stats[monthKey].parType[type] = (stats[monthKey].parType[type] || 0) + paiement.montant;
      } else {
        // Compter aussi les paiements hors période
        if (!otherMonths[monthKey]) {
          otherMonths[monthKey] = { total: 0, count: 0, parType: { inscription: 0, formation: 0, autre: 0 } };
        }
        otherMonths[monthKey].total += paiement.montant;
        otherMonths[monthKey].count += 1;

        const type = paiement.typePaiement || 'autre';
        otherMonths[monthKey].parType[type] = (otherMonths[monthKey].parType[type] || 0) + paiement.montant;
      }
    });

    // Fusionner les statistiques
    Object.keys(otherMonths).forEach(key => {
      // Ajouter les autres mois à la fin
      stats[key] = otherMonths[key];
    });

    return stats;
  };

  const monthlyStats = calculateMonthlyStats();

  const totalRevenue = Object.values(monthlyStats).reduce((sum, m) => sum + m.total, 0);
  const totalPaiements = Object.values(monthlyStats).reduce((sum, m) => sum + m.count, 0);
  const moyenneMensuelle = totalRevenue / 12;

  const formatMoney = (amount) =>
    new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);

  // Fonction d'export Excel
  const exportToExcel = () => {
    let csvContent = '\uFEFF';

    csvContent += 'RAPPORT DES REVENUS MENSUELS\n';
    csvContent += `Année scolaire,${anneeScolaire}\n`;
    csvContent += `Date d'export,${new Date().toLocaleDateString('fr-FR')}\n`;
    csvContent += '\n';

    csvContent += 'RÉSUMÉ GÉNÉRAL\n';
    csvContent += 'Indicateur,Valeur\n';
    csvContent += `Revenu Total,${formatMoney(totalRevenue)} MAD\n`;
    csvContent += `Total Paiements,${totalPaiements}\n`;
    csvContent += `Moyenne Mensuelle,${formatMoney(moyenneMensuelle)} MAD\n`;
    csvContent += '\n\n';

    csvContent += 'DÉTAILS MENSUELS\n';
    csvContent += 'Mois,Revenus (MAD),Nombre de paiements,Inscriptions (MAD),Formations (MAD),Autres (MAD)\n';

    months.forEach(month => {
      const stats = monthlyStats[month.key];
      csvContent += `"${month.label}",${formatMoney(stats.total)},${stats.count},${formatMoney(stats.parType.inscription || 0)},${formatMoney(stats.parType.formation || 0)},${formatMoney(stats.parType.autre || 0)}\n`;
    });

    csvContent += `TOTAL,${formatMoney(totalRevenue)},${totalPaiements},,,\n`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const fileName = `Revenus_Mensuels_${anneeScolaire.replace('/', '-')}_${new Date().toLocaleDateString('fr-FR').replace(/\//g, '-')}.csv`;
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div style={{ background: '#fff', padding: '2rem', fontFamily: 'Arial, sans-serif' }}>
      {/* En-tête */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
        paddingBottom: '0.5rem',
        borderBottom: '2px solid #000'
      }}>
        <h1 style={{
          fontSize: '1.25rem',
          fontWeight: 'bold',
          color: '#000',
          margin: 0
        }}>
          Revenus Mensuels - {anneeScolaire}
        </h1>
        <button
          onClick={exportToExcel}
          style={{
            padding: '0.5rem 1rem',
            background: '#217346',
            color: '#fff',
            border: '1px solid #1a5c37',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '0.9rem'
          }}
          onMouseOver={(e) => e.target.style.background = '#1a5c37'}
          onMouseOut={(e) => e.target.style.background = '#217346'}
        >
          📊 Exporter Excel
        </button>
      </div>

      {/* Sélecteur d'année */}
      <div style={{ marginBottom: '1.5rem' }}>
        <label style={{ fontWeight: '600', marginRight: '0.5rem' }}>Année scolaire:</label>
        <select
          value={anneeScolaire}
          onChange={(e) => setAnneeScolaire(e.target.value)}
          style={{
            padding: '0.5rem',
            border: '1px solid #000',
            borderRadius: '4px',
            fontWeight: '600'
          }}
        >
          <option value="2025/2026">2025/2026</option>
          <option value="2024/2025">2024/2025</option>
          {anneesDisponibles
            .filter(a => a !== '2025/2026' && a !== '2024/2025')
            .map(annee => (
              <option key={annee} value={annee}>{annee}</option>
            ))
          }
        </select>
      </div>

      {/* Statistiques globales */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 'bold', marginBottom: '0.75rem' }}>
          Résumé Général
        </h3>
        <table style={{
          width: '100%',
          maxWidth: '600px',
          borderCollapse: 'collapse',
          border: '1px solid #000'
        }}>
          <tbody>
            <tr style={{ background: '#f0f0f0' }}>
              <td style={{ padding: '0.5rem', border: '1px solid #000', fontWeight: 'bold' }}>
                Revenu Total
              </td>
              <td style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'right', fontWeight: 'bold' }}>
                {formatMoney(totalRevenue)} MAD
              </td>
            </tr>
            <tr>
              <td style={{ padding: '0.5rem', border: '1px solid #000', fontWeight: 'bold' }}>
                Total Paiements
              </td>
              <td style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'right', fontWeight: 'bold' }}>
                {totalPaiements}
              </td>
            </tr>
            <tr style={{ background: '#fafafa' }}>
              <td style={{ padding: '0.5rem', border: '1px solid #000', fontWeight: 'bold' }}>
                Moyenne Mensuelle
              </td>
              <td style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'right', fontWeight: 'bold' }}>
                {formatMoney(moyenneMensuelle)} MAD
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Tableau des revenus mensuels */}
      <div>
        <h3 style={{ fontSize: '1rem', fontWeight: 'bold', marginBottom: '0.75rem' }}>
          Revenus par Mois
        </h3>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          border: '1px solid #000'
        }}>
          <thead>
            <tr style={{ background: '#f0f0f0' }}>
              <th style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'left', fontWeight: 'bold' }}>
                Mois
              </th>
              <th style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'center', fontWeight: 'bold' }}>
                Revenus (MAD)
              </th>
              <th style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'center', fontWeight: 'bold' }}>
                Nombre de paiements
              </th>
              <th style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'center', fontWeight: 'bold' }}>
                Inscriptions
              </th>
              <th style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'center', fontWeight: 'bold' }}>
                Formations
              </th>
              <th style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'center', fontWeight: 'bold' }}>
                Autres
              </th>
            </tr>
          </thead>
          <tbody>
            {months.map((month, index) => {
              const stats = monthlyStats[month.key];
              return (
                <tr key={month.key} style={{ background: index % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <td style={{ padding: '0.5rem', border: '1px solid #000' }}>
                    {month.label}
                  </td>
                  <td style={{
                    padding: '0.5rem',
                    border: '1px solid #000',
                    textAlign: 'right',
                    fontWeight: 'bold'
                  }}>
                    {formatMoney(stats.total)}
                  </td>
                  <td style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'center', fontWeight: 'bold' }}>
                    {stats.count}
                  </td>
                  <td style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'right', fontWeight: 'bold' }}>
                    {formatMoney(stats.parType.inscription || 0)}
                  </td>
                  <td style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'right', fontWeight: 'bold' }}>
                    {formatMoney(stats.parType.formation || 0)}
                  </td>
                  <td style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'right', fontWeight: 'bold' }}>
                    {formatMoney(stats.parType.autre || 0)}
                  </td>
                </tr>
              );
            })}
            <tr style={{ background: '#e0e0e0', fontWeight: 'bold' }}>
              <td style={{ padding: '0.5rem', border: '1px solid #000' }}>
                TOTAL
              </td>
              <td style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'right' }}>
                {formatMoney(totalRevenue)}
              </td>
              <td style={{ padding: '0.5rem', border: '1px solid #000', textAlign: 'center' }}>
                {totalPaiements}
              </td>
              <td colSpan="3" style={{ padding: '0.5rem', border: '1px solid #000' }}></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MonthlyRevenueTracker;